import { TitleCasePipe } from '@angular/common';
import {
  Component,
  effect,
  EventEmitter,
  input,
  InputSignal,
  Output,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  OnInit
} from '@angular/core';
import { ProviderService, SchoolsService } from '@k12/app-services';
import type { ComponentsReview, EmployeeSchoolOption, InputsReview, ProgramReview, PromptInputOption, SchoolOption, SchoolTypeResponse, SubService } from "@k12/models";
import { FilterValueArray, OrderByPipe } from "@k12/pipes";
import { NoticeComponent } from "@k12/ui-primitives";
import { lea, states, counties, gradeLevels } from '../basic-info-const';
import { InputDataType, SUB_SELECT_DISCRIMINATOR_LABEL } from "@k12/models";
import { ssnLast4Digits } from "@k12/validators";

/** Shown when an SSN reaches review without four usable digits, so no partial number leaks. */
const FULLY_MASKED_SSN = 'XXX-XX-XXXX';

/** The renderer submits every datepicker as `yyyy-MM-dd`; a trailing time part is tolerated. */
const STORED_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})/;

const DATE_DATA_TYPES: ReadonlySet<string> = new Set([InputDataType.Date, InputDataType.FullDate, InputDataType.ShortDate]);

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [OrderByPipe, FilterValueArray, NoticeComponent, TitleCasePipe],
  providers: [],

  selector: 'lib-pc-review',
  styleUrl: './pc-review.component.scss',
  templateUrl: './pc-review.component.html'
})
export class PcReviewComponent implements OnInit {
  // editing: InputSignal<boolean> = input(true);
  enrollmentBuilderView: InputSignal<boolean> = input(true);
  isAdmin: InputSignal<boolean> = input(false);
  isIneligible: InputSignal<boolean> = input(false);
  reviewData: InputSignal<ProgramReview> = input(<ProgramReview>{});

  @Output() editSectionOutput = new EventEmitter();

  public showBanner: WritableSignal<boolean> = signal(true);

  protected readonly discriminatorLabel = SUB_SELECT_DISCRIMINATOR_LABEL;

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly providerService = inject(ProviderService);
  private readonly schoolsService = inject(SchoolsService);

  private readonly countyMap = new Map<string, string>();
  private readonly degreeSchoolMap = new Map<string, string>();
  private readonly gradeLevelMap = new Map<string, string>();
  private readonly schoolMap = new Map<string, string>();
  private readonly schoolDistrictMap = new Map<string, string>();
  private readonly subServiceMap = new Map<string, string>();
  private readonly stateMap = new Map<string, string>();
  private readonly tutoringSubServiceType = 1;
  private readonly therapySubServiceType = 2;

  constructor() {
    effect(() => {
      this.showBanner.set(this.enrollmentBuilderView());
    });
  }

  public ngOnInit(): void {
    this.seedStaticDropdownMaps();
    this.loadDynamicDropdownMaps();
  }

  public editSection(id: string, token: string) {
    this.editSectionOutput.emit({ id: id, token: token });
  }

  // Radio/checkbox store one boolean per option. Review has no componentType, so a component with
  // multiple boolean values is treated as that pattern: only show the selected (true) options.
  public visibleInputs(component: ComponentsReview): InputsReview[] {
    const allInputs = component?.inputs ?? [];
    const answers = allInputs.filter((input) => input.label !== SUB_SELECT_DISCRIMINATOR_LABEL);
    const inputs = answers.length > 0 ? answers : allInputs;
    const booleanInputs = inputs.filter((input) => this.isBooleanValue(input.value));
    if (booleanInputs.length > 1) {
      return booleanInputs.filter((input) => this.isTrueValue(input.value));
    }
    return inputs;
  }

  // Skipped optional radio/checkbox groups (all false / no submitted inputs) have nothing to list.
  public hasNoResponse(component: ComponentsReview): boolean {
    return this.visibleInputs(component).length === 0;
  }

  // Display-only mapping; underlying review/API values stay "true"/"false".
  public formatDisplayValue(value: string | null | undefined): string {
    if (this.isTrueValue(value)) {
      return 'Yes';
    }
    if (this.isFalseValue(value)) {
      return 'No';
    }
    return value ?? '';
  }

  /**
   * Formats the display value for a given input in the review component.
   * This method handles dates, boolean values, inline options, and known dropdowns.
   *
   * @param input - The input object containing the value to format.
   * @returns The formatted display value as a string.
   */
  public formatReviewValue(input: InputsReview): string {
    const value = input?.value;
    const normalized = value === null || value === undefined ? '' : String(value);

    if (this.isDateInput(input)) {
      return this.formatDateValue(normalized, input.dataType);
    }

    if (input.label === 'Yes' || input.label === 'No') {
      return input.label;
    } else {
      if (this.isTrueValue(normalized)) return 'Yes';
      if (this.isFalseValue(normalized)) return 'No';
    }

    const inlineLabel = this.getLabelFromInputOptions(input, normalized);
    if (inlineLabel) return inlineLabel;

    const dropdownLabel = this.getLabelFromKnownDropdowns(input, normalized);
    if (dropdownLabel) return dropdownLabel;

    return normalized;
  }

  public isFileInput(input: InputsReview): boolean {
    return input?.dataType === InputDataType.File;
  }

  /** True for inputs authored as a full SSN, whose value must never be rendered in the clear. */
  public isSsnInput(input: InputsReview): boolean {
    return input?.dataType === InputDataType.SSN9;
  }

  /**
   * Renders an SSN as `XXX-XX-1234`, matching how the rest of the app shows a stored SSN.
   *
   * The API already masks `ssn9` values before they leave the server, so this is normally an
   * identity transform over `XXX-XX-1234`. Re-deriving it here keeps review safe against a payload
   * that still carries digits -- an older API build, or a cached response from before that change.
   */
  public maskSsn(value: string | null | undefined): string {
    if (value == null || value.trim() === '') {
      return '';
    }

    const last4 = ssnLast4Digits(value);
    return last4.length === 4 ? `XXX-XX-${last4}` : FULLY_MASKED_SSN;
  }

  private isBooleanValue(value: string | null | undefined): boolean {
    return this.isTrueValue(value) || this.isFalseValue(value);
  }

  private isDateInput(input: InputsReview): boolean {
    return DATE_DATA_TYPES.has(input?.dataType ?? '');
  }

  /**
   * Shows a stored `yyyy-MM-dd` the way the prompt's datepicker did: MM/DD/YYYY, or MM/DD for short
   * dates. Reordered as text rather than through `Date`, so no timezone can shift the day. A value in
   * any other shape is passed through rather than guessed at.
   */
  private formatDateValue(value: string, dataType: string | undefined): string {
    const match = STORED_DATE_PATTERN.exec(value.trim());
    if (!match) {
      return value;
    }

    const [, year, month, day] = match;
    return dataType === InputDataType.ShortDate ? `${month}/${day}` : `${month}/${day}/${year}`;
  }

  private isTrueValue(value: string | null | undefined): boolean {
    return String(value).trim().toLowerCase() === 'true';
  }

  private isFalseValue(value: string | null | undefined): boolean {
    return String(value).trim().toLowerCase() === 'false';
  }

  /**
   * Seeds the static dropdown maps with predefined values for counties, grade levels, school districts, and states.
   * This method populates the corresponding maps with key-value pairs for quick lookup of display labels.
   * The keys are the string representations of the values, and the values are the display labels.
   *
   * @remarks
   * This method is called during component initialization to ensure that the static dropdown maps are ready for use.
   * It does not return any value.
   *
   * @returns void
   */
  private seedStaticDropdownMaps(): void {
    counties.forEach((county) => this.countyMap.set(String(county), county));
    gradeLevels.forEach((grade) => this.gradeLevelMap.set(String(grade), grade));
    lea.forEach((item) => this.schoolDistrictMap.set(String(item.leaId), item.leaName));
    states.forEach((state) => this.stateMap.set(String(state.code), state.name));
  }

  // Loads dynamic dropdown maps for schools, degree schools, subservices and grade levels from the respective services.
  private loadDynamicDropdownMaps(): void {
    this.schoolsService.getSchoolsForDropdown('', true).subscribe((res) => {
      res.entity.forEach((s: SchoolOption) => this.schoolMap.set(String(s.id), s.name));
      this.cdr.markForCheck();
    });

    this.providerService.getSchools().subscribe((schools: EmployeeSchoolOption[]) => {
      schools.forEach((s) => this.degreeSchoolMap.set(String(s.id), `${s.schoolName}`));
      this.cdr.markForCheck();
    });

    this.schoolsService.getGradeLevelsForDropdown().subscribe((res: SchoolTypeResponse) => {
      this.applyGradeLevelsFromService(res);
      this.cdr.markForCheck();
    });

    const labels = this.getAllReviewLabels();
    if (labels.some((label) => label.includes('tutoring'))) {
      this.providerService.getEmployeeSubServices(this.tutoringSubServiceType).subscribe((rows: SubService[]) => {
        this.addSubServicesToMap(rows);
        this.cdr.markForCheck();
      });
    }

    if (labels.some((label) => label.includes('therapy'))) {
      this.providerService.getEmployeeSubServices(this.therapySubServiceType).subscribe((rows: SubService[]) => {
        this.addSubServicesToMap(rows);
        this.cdr.markForCheck();
      });
    }
  }

  /**
   * Gets the label for a given input and raw value from the input's options.
   *
   * @param input - The input object containing options.
   * @param rawValue - The raw value to find the corresponding label for.
   * @returns The label corresponding to the raw value, or an empty string if not found.
   */
  private getLabelFromInputOptions(input: InputsReview, rawValue: string): string {
    const options = (input.options ?? []) as unknown[];

    const objectOptions = options.filter((option): option is PromptInputOption => this.isPromptInputOption(option));
    if (objectOptions.length > 0) {
      const match = objectOptions.find((opt) => String(opt.value ?? opt.label) === rawValue);
      return match?.label ?? '';
    }

    const stringOptions = options.filter((option): option is string => typeof option === 'string');
    const stringMatch = stringOptions.find((opt) => opt === rawValue);
    return stringMatch ?? '';
  }

  // Type guard to check if an unknown option is a PromptInputOption.
  private isPromptInputOption(option: unknown): option is PromptInputOption {
    return typeof option === 'object' && option !== null && ('label' in option || 'value' in option);
  }

  // Retrieves the label for a known dropdown based on the input's placeholder and label.
  private getLabelFromKnownDropdowns(input: InputsReview, rawValue: string): string {
    const key = this.getDropdownKey(input);

    if (key.includes('county')) return this.countyMap.get(rawValue) ?? '';
    if (key.includes('grade')) return this.gradeLevelMap.get(rawValue) ?? '';
    if (key.includes('district')) return this.schoolDistrictMap.get(rawValue) ?? '';
    if (key.includes('state')) return this.stateMap.get(rawValue) ?? '';
    if (key.includes('degree')) return this.degreeSchoolMap.get(rawValue) ?? '';
    if (key.includes('school')) return this.schoolMap.get(rawValue) ?? '';
    if (key.includes('tutoring') || key.includes('therapy')) return this.mapSubServiceValues(rawValue);

    return '';
  }

  // Generates a unique key for a dropdown based on the input's placeholder and label.
  private getDropdownKey(input: InputsReview): string {
    const label = String((input as { label?: string })?.label ?? '').toLowerCase();
    const placeholder = String((input as { placeholder?: string })?.placeholder ?? '').toLowerCase();
    return `${placeholder} ${label}`.trim();
  }

  // Applies the grade levels from the service response to the gradeLevelMap for display purposes.
  private applyGradeLevelsFromService(response: SchoolTypeResponse): void {
    type GradeLevelRow = {
      id?: string | number;
      value?: string | number;
      name?: string;
      label?: string;
    };

    const rows = (response as { entity?: GradeLevelRow[] }).entity ?? [];
    this.gradeLevelMap.clear();

    rows.forEach((row) => {
      const rawId = row.id ?? row.value;
      const label = row.name ?? row.label ?? (rawId !== undefined ? String(rawId) : '');

      if (!label) {
        return;
      }

      // Match both persisted id values and label-only values.
      if (rawId !== undefined && rawId !== null && String(rawId).trim().length > 0) {
        this.gradeLevelMap.set(String(rawId), label);
      }
      this.gradeLevelMap.set(label, label);
    });
  }

  // Adds sub-services to the subServiceMap for quick lookup of display labels based on id, name, or normalized token.
  private addSubServicesToMap(rows: SubService[]): void {
    rows.forEach((row) => {
      const idKey = String(row.id ?? '');
      const name = String(row.name ?? '').trim();
      if (!name) {
        return;
      }

      if (idKey) {
        this.subServiceMap.set(idKey, name); // id -> label
      }

      this.subServiceMap.set(name.toLowerCase(), name); // exact name -> label
      this.subServiceMap.set(this.normalizeSubServiceToken(name), name); // token -> label
    });
  }

  // Normalizes a sub-service name to a token format (lowercase, underscores, no leading/trailing underscores).
  private normalizeSubServiceToken(value: string): string {
    let normalized = value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+/, '');

    while (normalized.endsWith('_')) {
      normalized = normalized.slice(0, -1);
    }

    return normalized;
  }

  // Retrieves all labels from the review data for sub-service detection.
  private getAllReviewLabels(): string[] {
    const prompts = this.reviewData()?.prompts ?? [];
    return prompts.flatMap((prompt) =>
      (prompt.components ?? []).flatMap((component) =>
        (component.inputs ?? []).map((input) =>
          `${String(input.label ?? '').toLowerCase()} ${String((input as { placeholder?: string }).placeholder ?? '').toLowerCase()}`
        )
      )
    );
  }

  // Maps a raw sub-service value to its corresponding display label using the subServiceMap.
  private mapSubServiceValues(rawValue: string): string {
    const values = rawValue
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    if (values.length === 0) {
      return '';
    }

    return values
      .map((value) => {
        return (
          this.subServiceMap.get(value) ??
          this.subServiceMap.get(value.toLowerCase()) ??
          this.subServiceMap.get(this.normalizeSubServiceToken(value)) ??
          value
        );
      })
      .join(', ');
  }
}
